import { DEFAULT_IRT_CONTROLLED_VISITS_CONFIG } from './constants'

const toIdArray = value => (Array.isArray(value) ? value : [])

const toIdString = id => (id == null ? '' : String(id))

/**
 * Returns a normalized irt_controlled_visits config with backward-compatible defaults.
 * When the key is absent from study.config, enabled is false and arrays are empty.
 */
export const getIrtControlledVisitsConfig = (config = {}) => {
  const raw = config?.irt_controlled_visits
  if (raw == null) {
    return { ...DEFAULT_IRT_CONTROLLED_VISITS_CONFIG }
  }
  return {
    enabled: Boolean(raw.enabled),
    irt_visit_ids: toIdArray(raw.irt_visit_ids).map(toIdString).filter(Boolean),
    irt_role_ids: toIdArray(raw.irt_role_ids).map(toIdString).filter(Boolean),
  }
}

export const isIrtControlledVisitsEnabled = (config = {}) => getIrtControlledVisitsConfig(config).enabled

/** Study version is Live (URL segment or version list status). */
export const isStudyVersionLive = (studyVersionList, studyVersionID) => {
  if (toIdString(studyVersionID).toLowerCase() === 'live') {
    return true
  }
  if (!studyVersionList?.length) {
    return false
  }
  const currentVersion = studyVersionList.find(version => version?.version_id === studyVersionID)
  return currentVersion?.version_status?.toLowerCase() === 'live'
}

export const isRoleInIrtControlledList = (roleId, configuredRoleIds = []) => {
  if (roleId == null || !configuredRoleIds.length) {
    return false
  }
  const roleIdStr = toIdString(roleId)
  return configuredRoleIds.some(id => toIdString(id) === roleIdStr)
}

/**
 * GOLD-12406 Option A: reuse hide_participants_instruments_tab to hide delivery/status UI only.
 * The Instruments tab and View Instrument Responses remain available for listed roles.
 */
export const shouldHideParticipantInstrumentStatus = (config = {}, roleId) => {
  const hiddenStatusRoleIds = config?.hide_participants_instruments_tab
  return isRoleInIrtControlledList(roleId, hiddenStatusRoleIds)
}

/** Template visit ids from GET .../study_schedules (study_visit_schedule). */
export const collectTemplateVisitIdsFromStudySchedule = (studyVisitSchedule = []) => {
  const ids = new Set()
  if (!Array.isArray(studyVisitSchedule)) {
    return []
  }

  studyVisitSchedule.forEach(arm => {
    arm?.visits?.forEach(visit => {
      if (visit?.id) ids.add(toIdString(visit.id))
    })
    arm?.phases?.forEach(phase => {
      phase?.cycles?.forEach(cycle => {
        if (cycle?.metadata?.visit_id) ids.add(toIdString(cycle.metadata.visit_id))
        cycle?.visits?.forEach(visit => {
          if (visit?.id) ids.add(toIdString(visit.id))
        })
      })
    })
  })

  return [...ids]
}

export const getVisitIdsForIrtMatching = ({
  visitId,
  participantVisitMetadata = {},
  studyScheduleVisitId,
} = {}) => {
  const candidates = [
    visitId,
    studyScheduleVisitId,
    participantVisitMetadata?.visit_id,
    participantVisitMetadata?.study_visit_template_id,
    participantVisitMetadata?.study_schedule_visit_id,
    participantVisitMetadata?.visit_template_id,
    participantVisitMetadata?.template_visit_id,
    participantVisitMetadata?.relative_visit_id,
  ].filter(id => id != null && id !== '')

  return [...new Set(candidates.map(toIdString))]
}

/**
 * Prefer template visit ids from study_visit_schedule when schedule is loaded.
 * irt_visit_ids are configured against template ids (e.g. phases[].cycles[].visits[].id).
 */
export const resolveVisitIdsForIrtMatching = ({
  visitId,
  participantVisitMetadata,
  studyScheduleVisitId,
  studyVisitSchedule,
} = {}) => {
  const allCandidates = getVisitIdsForIrtMatching({
    visitId,
    participantVisitMetadata,
    studyScheduleVisitId,
  })
  const templateIdsFromSchedule = collectTemplateVisitIdsFromStudySchedule(studyVisitSchedule)

  if (!templateIdsFromSchedule.length) {
    return allCandidates
  }

  const templateSet = new Set(templateIdsFromSchedule)
  const templateMatches = allCandidates.filter(id => templateSet.has(id))
  if (templateMatches.length) {
    return templateMatches
  }

  const visitIdStr = toIdString(visitId)
  const nonInstanceCandidates = allCandidates.filter(id => id !== visitIdStr)
  if (nonInstanceCandidates.length) {
    return nonInstanceCandidates
  }

  return []
}

export const isVisitInIrtControlledList = (visitIds, configuredVisitIds = []) => {
  if (!configuredVisitIds.length) {
    return false
  }
  const configuredSet = new Set(configuredVisitIds.map(toIdString))
  const idsToMatch = Array.isArray(visitIds) ? visitIds : [visitIds]
  return idsToMatch.some(id => configuredSet.has(toIdString(id)))
}

/**
 * True when IRT restrictions apply: live study, feature enabled, role and visit are configured.
 */
export const isIrtControlledVisitRestricted = ({
  config,
  studyVersionList,
  studyVersionID,
  roleId,
  visitId,
  participantVisitMetadata,
  studyScheduleVisitId,
  studyVisitSchedule,
}) => {
  if (!isStudyVersionLive(studyVersionList, studyVersionID)) {
    return false
  }

  const irtConfig = getIrtControlledVisitsConfig(config)
  if (!irtConfig.enabled) {
    return false
  }

  if (!isRoleInIrtControlledList(roleId, irtConfig.irt_role_ids)) {
    return false
  }

  const visitIds = resolveVisitIdsForIrtMatching({
    visitId,
    participantVisitMetadata,
    studyScheduleVisitId,
    studyVisitSchedule,
  })
  return isVisitInIrtControlledList(visitIds, irtConfig.irt_visit_ids)
}
